import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Puzzle, Brain, Shuffle, Clock, Trophy, Star,
  Play, RotateCcw, Check, X, Volume2, Globe, ArrowRight, ArrowLeft
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Flag Image component
const FlagImage = ({ countryCode, className = "w-5 h-4" }) => {
  if (!countryCode) return <Globe className={`${className} text-muted-foreground`} />;
  return (
    <img 
      src={`https://flagcdn.com/w40/${countryCode}.png`}
      alt={countryCode}
      className={`${className} object-cover rounded`}
      loading="lazy"
    />
  );
};

// Language code to country code mapping
const getCountryCode = (langCode) => {
  const mapping = {
    'no': 'no', 'nb': 'no', 'nn': 'no', 'nor': 'no',
    'en': 'gb', 'eng': 'gb',
    'es': 'es', 'spa': 'es',
    'fr': 'fr', 'fra': 'fr',
    'de': 'de', 'deu': 'de',
    'it': 'it', 'ita': 'it',
    'pt': 'br', 'por': 'br',
    'ru': 'ru', 'rus': 'ru',
    'zh': 'cn', 'zho': 'cn',
    'ja': 'jp', 'jpn': 'jp',
    'ko': 'kr', 'kor': 'kr',
    'ar': 'sa', 'ara': 'sa',
    'he': 'il', 'heb': 'il',
    'sw': 'tz', 'swa': 'tz',
  };
  return mapping[langCode?.toLowerCase()] || langCode?.slice(0, 2)?.toLowerCase();
};


// ===============================
// SENTENCE BUILDER GAME COMPONENT
// ===============================
const SentenceBuilderGame = ({ language, onBack }) => {
  const { t } = useI18n();
  const [gameData, setGameData] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedWords, setSelectedWords] = useState([]);
  const [availableWords, setAvailableWords] = useState([]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [difficulty, setDifficulty] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const startGame = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/games/sentence-builder`,
        { language_id: language.language_id, game_type: 'sentence_builder', difficulty },
        { withCredentials: true }
      );
      setGameData(res.data);
      setCurrentIndex(0);
      setScore(0);
      setTimeLeft(res.data.time_limit);
      setGameOver(false);
      setShowResult(false);
      
      // Initialize first sentence
      if (res.data.sentences.length > 0) {
        setAvailableWords([...res.data.sentences[0].scrambled_words]);
        setSelectedWords([]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start game');
    } finally {
      setLoading(false);
    }
  };

  // Timer
  useEffect(() => {
    if (!gameData || gameOver || timeLeft <= 0) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setGameOver(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [gameData, gameOver, timeLeft]);

  const selectWord = (word, index) => {
    setSelectedWords([...selectedWords, word]);
    const newAvailable = [...availableWords];
    newAvailable.splice(index, 1);
    setAvailableWords(newAvailable);
  };

  const unselectWord = (word, index) => {
    setAvailableWords([...availableWords, word]);
    const newSelected = [...selectedWords];
    newSelected.splice(index, 1);
    setSelectedWords(newSelected);
  };

  const checkSentence = () => {
    const currentSentence = gameData.sentences[currentIndex];
    const isMatch = selectedWords.join(' ') === currentSentence.correct_order.join(' ');
    
    setIsCorrect(isMatch);
    setShowResult(true);
    
    if (isMatch) {
      setScore(prev => prev + 10 + Math.floor(timeLeft / 10));
    }
    
    // Move to next after a delay
    setTimeout(() => {
      if (currentIndex + 1 < gameData.sentences.length) {
        setCurrentIndex(currentIndex + 1);
        setAvailableWords([...gameData.sentences[currentIndex + 1].scrambled_words]);
        setSelectedWords([]);
        setShowResult(false);
      } else {
        setGameOver(true);
      }
    }, 1500);
  };

  const resetCurrentSentence = () => {
    if (gameData && gameData.sentences[currentIndex]) {
      setAvailableWords([...gameData.sentences[currentIndex].scrambled_words]);
      setSelectedWords([]);
    }
  };

  if (!gameData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Puzzle className="h-6 w-6 text-blue-500" />
            {t('gamesHub.sentenceBuilder')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            {t('gamesHub.sentenceBuilderDesc')}
          </p>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">{t('gamesHub.difficulty')}</label>
              <Select value={difficulty} onValueChange={setDifficulty}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">{t('gamesHub.easy')} (5 {t('gamesHub.sentences')})</SelectItem>
                  <SelectItem value="medium">{t('gamesHub.medium')} (7 {t('gamesHub.sentences')})</SelectItem>
                  <SelectItem value="hard">{t('gamesHub.hard')} (10 {t('gamesHub.sentences')})</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={startGame} disabled={loading} className="gap-2">
              <Play className="h-4 w-4" />
              {loading ? t('common.loading') : t('gamesHub.startGame')}
            </Button>
          </div>
          <Button variant="ghost" onClick={onBack} className="w-full mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('gamesHub.backToGames')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Bar */}
      <div className="flex items-center justify-between p-4 rounded-lg bg-card border">
        <div className="flex items-center gap-4">
          <Badge variant="outline" className="text-lg px-3 py-1">
            {currentIndex + 1} / {gameData.sentences.length}
          </Badge>
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            <span className="font-bold">{score}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-muted-foreground" />
          <span className={`font-mono ${timeLeft < 30 ? 'text-red-500' : ''}`}>
            {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
          </span>
        </div>
      </div>

      {!gameOver ? (
        <Card>
          <CardContent className="pt-6 space-y-6">
            {/* Translation hint */}
            <div className="text-center p-4 rounded-lg bg-muted/50">
              <p className="text-sm text-muted-foreground mb-1">{t('gamesHub.buildSentence')}</p>
              <p className="text-lg font-medium">{gameData.sentences[currentIndex]?.translation}</p>
            </div>

            {/* Selected words (sentence being built) */}
            <div className="min-h-[60px] p-4 rounded-lg border-2 border-dashed border-primary/30 flex flex-wrap gap-2 items-center justify-center">
              {selectedWords.length === 0 ? (
                <span className="text-muted-foreground">{t('gamesHub.tapWordsBelow')}</span>
              ) : (
                selectedWords.map((word, idx) => (
                  <button
                    key={idx}
                    onClick={() => unselectWord(word, idx)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all
                      ${showResult 
                        ? (isCorrect ? 'bg-green-500 text-white' : 'bg-red-500 text-white')
                        : 'bg-primary text-primary-foreground hover:bg-primary/90'
                      }`}
                  >
                    {word}
                  </button>
                ))
              )}
            </div>

            {/* Available words to select */}
            <div className="flex flex-wrap gap-2 justify-center">
              {availableWords.map((word, idx) => (
                <button
                  key={idx}
                  onClick={() => selectWord(word, idx)}
                  disabled={showResult}
                  className="px-4 py-2 rounded-lg bg-secondary hover:bg-secondary/80 font-medium transition-all"
                >
                  {word}
                </button>
              ))}
            </div>

            {/* Action buttons */}
            <div className="flex justify-center gap-4">
              <Button 
                variant="outline" 
                onClick={resetCurrentSentence}
                disabled={showResult || selectedWords.length === 0}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                {t('gamesHub.reset')}
              </Button>
              <Button 
                onClick={checkSentence}
                disabled={showResult || availableWords.length > 0}
              >
                <Check className="h-4 w-4 mr-2" />
                {t('gamesHub.check')}
              </Button>
            </div>

            {/* Result feedback */}
            {showResult && (
              <div className={`text-center p-4 rounded-lg ${isCorrect ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}`}>
                {isCorrect ? (
                  <p className="font-bold">Correct! +{10 + Math.floor(timeLeft / 10)} points</p>
                ) : (
                  <p>
                    <span className="font-bold">Not quite. </span>
                    Correct: {gameData.sentences[currentIndex]?.correct_order.join(' ')}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="text-center">
          <CardContent className="pt-6">
            <Trophy className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">{t('gamesHub.gameOver')}</h2>
            <p className="text-4xl font-bold text-primary mb-4">{score} {t('gamesHub.points')}</p>
            <div className="flex justify-center gap-4">
              <Button onClick={startGame}>{t('gamesHub.playAgain')}</Button>
              <Button variant="outline" onClick={onBack}>{t('gamesHub.backToGames')}</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};


// ===============================
// MEMORY MATCH GAME COMPONENT
// ===============================
const MemoryMatchGame = ({ language, onBack }) => {
  const { t } = useI18n();
  const [gameData, setGameData] = useState(null);
  const [cards, setCards] = useState([]);
  const [flippedIndices, setFlippedIndices] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState(new Set());
  const [moves, setMoves] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [difficulty, setDifficulty] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const startGame = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/games/memory-match`,
        { language_id: language.language_id, game_type: 'memory_match', difficulty },
        { withCredentials: true }
      );
      setGameData(res.data);
      setCards(res.data.cards.map((c, i) => ({ ...c, index: i, flipped: false })));
      setFlippedIndices([]);
      setMatchedPairs(new Set());
      setMoves(0);
      setTimeLeft(res.data.time_limit);
      setGameOver(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start game');
    } finally {
      setLoading(false);
    }
  };

  // Timer
  useEffect(() => {
    if (!gameData || gameOver || timeLeft <= 0) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setGameOver(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [gameData, gameOver, timeLeft]);

  // Check for win
  useEffect(() => {
    if (gameData && matchedPairs.size === gameData.pairs_count) {
      setGameOver(true);
    }
  }, [matchedPairs, gameData]);

  const flipCard = (index) => {
    if (isChecking || flippedIndices.includes(index) || matchedPairs.has(cards[index].pair_id)) {
      return;
    }

    const newFlipped = [...flippedIndices, index];
    setFlippedIndices(newFlipped);

    if (newFlipped.length === 2) {
      setMoves(prev => prev + 1);
      setIsChecking(true);

      const [first, second] = newFlipped;
      const firstCard = cards[first];
      const secondCard = cards[second];

      if (firstCard.pair_id === secondCard.pair_id) {
        // Match!
        setMatchedPairs(prev => new Set([...prev, firstCard.pair_id]));
        setFlippedIndices([]);
        setIsChecking(false);
      } else {
        // No match - flip back after delay
        setTimeout(() => {
          setFlippedIndices([]);
          setIsChecking(false);
        }, 1000);
      }
    }
  };

  const calculateScore = () => {
    const baseScore = matchedPairs.size * 100;
    const timeBonus = timeLeft * 2;
    const movePenalty = moves * 5;
    return Math.max(0, baseScore + timeBonus - movePenalty);
  };

  if (!gameData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-6 w-6 text-purple-500" />
            {t('gamesHub.memoryMatch')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            {t('gamesHub.memoryMatchDesc')}
          </p>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">{t('gamesHub.difficulty')}</label>
              <Select value={difficulty} onValueChange={setDifficulty}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">{t('gamesHub.easy')} (6 {t('gamesHub.pairs')})</SelectItem>
                  <SelectItem value="medium">{t('gamesHub.medium')} (10 {t('gamesHub.pairs')})</SelectItem>
                  <SelectItem value="hard">{t('gamesHub.hard')} (15 {t('gamesHub.pairs')})</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={startGame} disabled={loading} className="gap-2">
              <Play className="h-4 w-4" />
              {loading ? t('common.loading') : t('gamesHub.startGame')}
            </Button>
          </div>
          <Button variant="ghost" onClick={onBack} className="w-full mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('gamesHub.backToGames')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Calculate grid columns based on card count
  const gridCols = cards.length <= 12 ? 4 : cards.length <= 20 ? 5 : 6;

  return (
    <div className="space-y-4">
      {/* Stats Bar */}
      <div className="flex items-center justify-between p-4 rounded-lg bg-card border">
        <div className="flex items-center gap-4">
          <Badge variant="outline" className="text-lg px-3 py-1">
            {matchedPairs.size} / {gameData.pairs_count} pairs
          </Badge>
          <div className="flex items-center gap-2">
            <Shuffle className="h-5 w-5 text-muted-foreground" />
            <span>{moves} moves</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-muted-foreground" />
          <span className={`font-mono ${timeLeft < 30 ? 'text-red-500' : ''}`}>
            {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
          </span>
        </div>
      </div>

      {!gameOver ? (
        <div 
          className="grid gap-2 p-4"
          style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}
        >
          {cards.map((card, index) => {
            const isFlipped = flippedIndices.includes(index);
            const isMatched = matchedPairs.has(card.pair_id);
            
            return (
              <button
                key={card.id}
                onClick={() => flipCard(index)}
                disabled={isFlipped || isMatched || isChecking}
                className={`
                  aspect-square rounded-lg font-medium transition-all duration-300 transform
                  flex items-center justify-center p-2 text-center
                  ${isMatched 
                    ? 'bg-green-500 text-white scale-95' 
                    : isFlipped 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-secondary hover:bg-secondary/80 cursor-pointer'
                  }
                `}
                style={{
                  fontSize: card.content?.length > 10 ? '0.7rem' : card.content?.length > 6 ? '0.85rem' : '1rem',
                  minHeight: '60px'
                }}
              >
                {(isFlipped || isMatched) ? (
                  <span className="break-words">{card.content}</span>
                ) : (
                  <span className="text-2xl">?</span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <Card className="text-center">
          <CardContent className="pt-6">
            {matchedPairs.size === gameData.pairs_count ? (
              <>
                <Trophy className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2">{t('gamesHub.youWon')}</h2>
              </>
            ) : (
              <>
                <Clock className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2">{t('gamesHub.timesUp')}</h2>
              </>
            )}
            <p className="text-muted-foreground mb-2">
              {matchedPairs.size} {t('gamesHub.pairs')} matched in {moves} {t('gamesHub.moves')}
            </p>
            <p className="text-4xl font-bold text-primary mb-4">{calculateScore()} {t('gamesHub.points')}</p>
            <div className="flex justify-center gap-4">
              <Button onClick={startGame}>{t('gamesHub.playAgain')}</Button>
              <Button variant="outline" onClick={onBack}>{t('gamesHub.backToGames')}</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};


// ===============================
// MAIN GAMES HUB COMPONENT
// ===============================
export default function GamesHub() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [activeGame, setActiveGame] = useState(null);
  const [dailyLimits, setDailyLimits] = useState(null);

  useEffect(() => {
    fetchLanguages();
    fetchDailyLimits();
  }, []);

  const fetchLanguages = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/languages/user/me`, { withCredentials: true });
      setLanguages(res.data || []);
      if (res.data?.length > 0) {
        setSelectedLanguage(res.data[0]);
      }
    } catch (err) {
      console.error('Error fetching languages:', err);
    }
  };

  const fetchDailyLimits = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/limits/status`, { withCredentials: true });
      setDailyLimits(res.data?.limits?.games);
    } catch (err) {
      console.error('Error fetching limits:', err);
    }
  };

  const games = [
    {
      id: 'sentence_builder',
      title: t('gamesHub.sentenceBuilder'),
      description: t('gamesHub.sentenceBuilderDesc'),
      icon: Puzzle,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      id: 'memory_match',
      title: t('gamesHub.memoryMatch'),
      description: t('gamesHub.memoryMatchDesc'),
      icon: Brain,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
    },
  ];

  if (activeGame && selectedLanguage) {
    if (activeGame === 'sentence_builder') {
      return (
        <div className="min-h-screen bg-background p-4 md:p-6">
          <div className="max-w-4xl mx-auto">
            <SentenceBuilderGame 
              language={selectedLanguage} 
              onBack={() => setActiveGame(null)} 
            />
          </div>
        </div>
      );
    }
    if (activeGame === 'memory_match') {
      return (
        <div className="min-h-screen bg-background p-4 md:p-6">
          <div className="max-w-4xl mx-auto">
            <MemoryMatchGame 
              language={selectedLanguage} 
              onBack={() => setActiveGame(null)} 
            />
          </div>
        </div>
      );
    }
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-2">{t('gamesHub.title')}</h1>
          <p className="text-muted-foreground">
            {t('gamesHub.subtitle')}
          </p>
        </div>

        {/* Language selector */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="flex-1 w-full">
                <label className="text-sm font-medium mb-2 block">{t('gamesHub.selectLanguage')}</label>
                <Select 
                  value={selectedLanguage?.language_id || ''} 
                  onValueChange={(val) => {
                    const lang = languages.find(l => l.language_id === val);
                    setSelectedLanguage(lang);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('gamesHub.selectLanguage')} />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map(lang => (
                      <SelectItem key={lang.language_id} value={lang.language_id}>
                        <div className="flex items-center gap-2">
                          <FlagImage countryCode={getCountryCode(lang.language?.code || lang.code)} className="w-5 h-4" />
                          <span>{lang.language?.name || lang.name || 'Unknown'}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {dailyLimits && (
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">{t('gamesHub.gamesRemaining')}</p>
                  <p className="text-2xl font-bold text-primary">
                    {dailyLimits.remaining} / {dailyLimits.limit}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Games Grid */}
        <div className="grid md:grid-cols-2 gap-4">
          {games.map((game) => (
            <Card 
              key={game.id}
              className="hover:border-primary/50 transition-colors cursor-pointer group"
              onClick={() => selectedLanguage && setActiveGame(game.id)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${game.bgColor}`}>
                    <game.icon className={`h-8 w-8 ${game.color}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-1 group-hover:text-primary transition-colors">
                      {game.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {game.description}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {!selectedLanguage && (
          <p className="text-center text-muted-foreground">
            {t('gamesHub.selectLanguage')}
          </p>
        )}
      </div>
    </div>
  );
}
